import { DEFAULT_MOTIVATION_CONFIG } from './defaults';

export const hoursFromSeconds = (seconds) => Number(seconds || 0) / 3600;

export const normalizeOcc = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return n <= 1 ? n * 100 : n;
};

export const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const formatMoney = (value) =>
    `${roundMoney(value).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} р`;

export const formatHours = (value) =>
    `${(Number(value) || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ч`;

export const formatOcc = (value) => `${normalizeOcc(value).toFixed(2)}%`;

export const calcBasePay = (hours, tiers = []) => {
    const h = Number(hours) || 0;
    const sorted = [...tiers].sort((a, b) => {
        const av = a.maxHours == null ? Number.POSITIVE_INFINITY : Number(a.maxHours);
        const bv = b.maxHours == null ? Number.POSITIVE_INFINITY : Number(b.maxHours);
        return av - bv;
    });

    for (let i = 0; i < sorted.length; i += 1) {
        const tier = sorted[i];
        if (tier.mode === 'overflow') {
            const baseHours = Number(tier.baseHours) || 0;
            if (h >= baseHours) {
                return baseHours * Number(tier.baseRate || 0) + (h - baseHours) * Number(tier.rate || 0);
            }
            continue;
        }
        if (tier.maxHours == null || h < Number(tier.maxHours)) {
            return h * Number(tier.rate || 0);
        }
    }
    return 0;
};

export const PARTTIME_COEF = { Full: 1, Half: 0.5, Quarter: 0.25 };

export const parttimeCoefOf = (parttime) => PARTTIME_COEF[parttime] ?? 1;

export const resolveBonusPercent = (occPercent, config = DEFAULT_MOTIVATION_CONFIG) => {
    const occ = normalizeOcc(occPercent);
    const tiers = config.occBonusTiers || [];
    const match = tiers.find((tier) => {
        const from = Number(tier.occFrom) || 0;
        const to = Number(tier.occTo);
        const inclusiveTo = !Number.isFinite(to) || to >= 100;
        return occ >= from && (inclusiveTo ? occ <= 100 : occ < to);
    });
    if (match) return Number(match.percent) || 0;
    return Number(config.defaultBonusPercent) || 0;
};

export const calcPayroll = (input = {}, config = DEFAULT_MOTIVATION_CONFIG) => {
    const hoursPaid = hoursFromSeconds(input.paidTime);
    const hoursTotal = hoursFromSeconds(input.totalTime);
    const hoursRecalc = hoursTotal * Number(config.hoursCoefficient || 1);
    const basePay = calcBasePay(hoursRecalc, config.hourTiers);
    const bonusPercent = input.bonusPercent != null
        ? Number(input.bonusPercent)
        : resolveBonusPercent(input.occPercent, config);
    const bonusPay = basePay * (bonusPercent / 100);
    const allowance = Number(input.allowance) || 0;
    const schemeTotal = basePay + bonusPay + allowance;
    const parttime = input.parttime || 'Full';
    const coef = input.parttimeCoef != null ? Number(input.parttimeCoef) : parttimeCoefOf(parttime);
    const normFullHours = Number(input.normFullHours) || 0;
    const normHours = roundMoney(normFullHours * coef);
    const salaryFull = Number(config.contractMonthlySalary) || 0;
    const salaryApplied = roundMoney(salaryFull * coef);
    const contractPay = normHours > 0 ? (salaryApplied / normHours) * hoursPaid : 0;
    const isContract = input.contractType === 'Contract';
    const toPay = isContract ? contractPay : schemeTotal;

    return {
        hoursPaid: roundMoney(hoursPaid),
        hoursTotal: roundMoney(hoursTotal),
        hoursRecalc: roundMoney(hoursRecalc),
        rateApplied: roundMoney(hoursRecalc > 0 ? basePay / hoursRecalc : 0),
        basePay: roundMoney(basePay),
        bonusPercent: roundMoney(bonusPercent),
        bonusPay: roundMoney(bonusPay),
        allowance: roundMoney(allowance),
        schemeTotal: roundMoney(schemeTotal),
        normFullHours: roundMoney(normFullHours),
        normHours,
        parttime,
        parttimeCoef: coef,
        salaryApplied,
        contractPay: roundMoney(contractPay),
        toPay: roundMoney(toPay),
        payoutKind: isContract ? 'contract' : 'scheme',
        contractType: input.contractType || null,
    };
};

export const applyPayrollBonus = (calc = {}, patch = {}) => {
    const basePay = Number(calc.basePay) || 0;
    const allowance = Number(calc.allowance) || 0;
    let bonusPercent = Number(calc.bonusPercent) || 0;
    let bonusPay = Number(calc.bonusPay) || 0;

    if (patch.percent != null && patch.percent !== '') {
        bonusPercent = Number(patch.percent) || 0;
        bonusPay = roundMoney(basePay * (bonusPercent / 100));
    } else if (patch.amount != null && patch.amount !== '') {
        bonusPay = roundMoney(Number(patch.amount) || 0);
        bonusPercent = basePay > 0 ? roundMoney((bonusPay / basePay) * 100) : 0;
    }

    const schemeTotal = roundMoney(basePay + bonusPay + allowance);
    const contractPay = Number(calc.contractPay) || 0;
    const toPay = calc.payoutKind === 'contract'
        ? roundMoney(contractPay + bonusPay)
        : schemeTotal;

    return {
        ...calc,
        bonusPercent: roundMoney(bonusPercent),
        bonusPay,
        schemeTotal,
        toPay,
        bonusManual: true,
    };
};

export const motivationLabel = (version) => {
    if (!version) return '';
    if (typeof version === 'string' || typeof version === 'number') return String(version);
    const num = version.version != null && (typeof version.version === 'string' || typeof version.version === 'number')
        ? `v${version.version}`
        : '';
    const name = typeof version.name === 'string'
        ? version.name
        : (typeof version.comment === 'string' ? version.comment : '');
    return [num, name].filter(Boolean).join(' · ');
};
